import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect } from "react"

import useUser from "@hooks/useUser"

import { AppliedFilter, AppliedFilterInput } from "@organisms/ObjectsView/ObjectsView.types"

import defaultListFiltersConfig, { ListFiltersConfigType } from "@constants/defaultListFiltersConfig"

// Filters belong to the tab whatever the view type, so their entries are keyed without a view segment.
const listFiltersConfigAtom = atomWithStorage<ListFiltersConfigType>(
    "roopairs_list_filters_config",
    defaultListFiltersConfig,
    undefined,
    {
        getOnInit: true,
    },
)

const NO_FILTERS: AppliedFilter[] = []

export default function useListFilters(storageKey: string) {
    const [listFiltersConfig, setListFiltersConfig] = useAtom(listFiltersConfigAtom)
    const { user } = useUser()

    // A filter holds its company's ids, so each company keeps its own tabs; until the user loads there are none.
    const companyId = user?.service_company.id
    const companyTabs = companyId === undefined ? undefined : listFiltersConfig.companies[companyId]?.tabs

    useEffect(() => {
        if (listFiltersConfig.version !== defaultListFiltersConfig.version) {
            // A major version drops the stored filters; a minor one keeps them.
            const [storedMajor] = listFiltersConfig.version.split(".")
            const [defaultMajor] = defaultListFiltersConfig.version.split(".")

            setListFiltersConfig(
                storedMajor === defaultMajor
                    ? { ...listFiltersConfig, version: defaultListFiltersConfig.version }
                    : defaultListFiltersConfig,
            )
        }
    }, [])

    const setAppliedFilters = (update: (previousFilters: AppliedFilter[]) => AppliedFilter[]) => {
        if (companyId !== undefined) {
            setListFiltersConfig((previousConfig) => {
                const previousTabs = previousConfig.companies[companyId]?.tabs ?? {}

                return {
                    ...previousConfig,
                    companies: {
                        ...previousConfig.companies,
                        [companyId]: {
                            tabs: {
                                ...previousTabs,
                                [storageKey]: { filters: update(previousTabs[storageKey]?.filters ?? NO_FILTERS) },
                            },
                        },
                    },
                }
            })
        }
    }

    const addAppliedFilters = (inputs: AppliedFilterInput[]) => {
        const filters: AppliedFilter[] = inputs.map((input) => ({ id: crypto.randomUUID(), ...input }))
        setAppliedFilters((previousFilters) => [...previousFilters, ...filters])

        return filters
    }

    const updateAppliedFilter = (id: AppliedFilter["id"], changes: Partial<AppliedFilterInput>) => {
        setAppliedFilters((previousFilters) =>
            previousFilters.map((filter) => (filter.id === id ? { ...filter, ...changes } : filter)),
        )
    }

    const removeAppliedFilter = (id: AppliedFilter["id"]) => {
        setAppliedFilters((previousFilters) => previousFilters.filter((filter) => filter.id !== id))
    }

    const clearAppliedFilters = () => {
        setAppliedFilters(() => NO_FILTERS)
    }

    // Read another tab's filters as persisted.
    const getAppliedFiltersForKey = (key: string) => companyTabs?.[key]?.filters ?? NO_FILTERS

    return {
        appliedFilters: companyTabs?.[storageKey]?.filters ?? NO_FILTERS,
        addAppliedFilters,
        updateAppliedFilter,
        removeAppliedFilter,
        clearAppliedFilters,
        getAppliedFiltersForKey,
    }
}
